import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useUser } from '../userContext';
import { notifySuccess } from '../utils/notify';

import SettingsSidebar from '../partials/settings/SettingsSidebar';
import Profile from './Profile';
import Users from './Users';
import Workspaces from './Workspaces';
import AppsPanel from '../partials/settings/AppsPanel';
import FeedbackPanel from '../partials/settings/FeedbackPanel';
import CreditPackModal from '../components/CreditPackModal';


// SaaS v1: settings → credits page for non-admin users. Same component
// the inline editor modal renders, but page-shaped (no Cancel chrome).
function CreditsAndBillingPage() {
  return (
    <div className="flex-1 p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold gch-text-primary mb-1">Credits</h2>
        <p className="text-sm gch-text-secondary">
          Free to build. First publish free. £39 each one after that. Your current balance is shown in the header.
        </p>
      </div>
      <div className="rounded-2xl border border-[var(--admin-border)] gch-bg-card max-w-3xl">
        <CreditPackModal />
      </div>
    </div>
  );
}

function Settings() {
  const { businessProfile, userProfile, switchActiveClient, isAdmin } = useUser();

  const isImpersonating = businessProfile?.id && userProfile?.companyId && businessProfile.id !== userProfile.companyId;
  const didSwitchRef = useRef(false);

  // Auto-revert to agency workspace when entering Settings while viewing a client workspace.
  // Users, Plans, and Workspaces all operate at the agency level.
  useEffect(() => {
    if (isImpersonating && !didSwitchRef.current) {
      didSwitchRef.current = true;
      switchActiveClient(null);
      notifySuccess('Switched back to your default workspace for settings.');
    }
  }, [isImpersonating]);

  return (
    <div className="gch-page gch-content-width">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight gch-text-primary">
          Settings
        </h1>
        <p className="text-sm mt-2 font-medium gch-text-secondary">
          Configure your account and preferences
        </p>
      </div>

      <div className="gch-bg-card border border-[var(--admin-border)] shadow-sm rounded-xl mb-8">
        <div className="flex flex-col md:flex-row md:-mr-px">
          <SettingsSidebar />
          <Routes>
            <Route path="account" element={<Profile />} />
            <Route path="plans" element={<CreditsAndBillingPage />} />
            {/* Users + Workspaces are legacy agency / team features, kept in
                code but only reachable by admins in v1. Non-admins URL-typing
                these paths bounce to the account tab. */}
            <Route path="users" element={isAdmin ? <Users /> : <Navigate to="/studio/settings/account" replace />} />
            <Route path="workspaces" element={isAdmin ? <Workspaces /> : <Navigate to="/studio/settings/account" replace />} />
            <Route path="apps" element={isAdmin ? <AppsPanel /> : <Navigate to="/studio/settings/account" replace />} />
            <Route path="help" element={<FeedbackPanel />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default Settings;
